import { useState, useEffect, useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import AgencyHeader from 'components/Layouts/components/AgencyHeader';
import AgencySidebar from './components/AgencySidebar';
import PaymentCardWarningModal, { PAYMENT_CARD_REMINDER_COOKIE } from '@/components/Common/PaymentCardWarningModal';
import { useGetCardDetails, usePayAgencyDues } from '@/apis/agency/billing/hook';
import { getCookie } from '@/utils/helperFunctions';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import PaymentDueModal from '../Common/PaymentDueModal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

type Props = {
  readonly children: JSX.Element;
};

export default function AgencyLayout({ children }: Props) {
  const theme = useTheme();
  const params = useParams();
  const path = params['*'];
  const isOnboardingFormEdit = path?.includes('onboarding-forms') && path?.split('/')[1];

  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isProfilePage = location.pathname.includes('profile');
  const isBillingPage = location.pathname.includes('billing');
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);
  const [showPaymentDue, setShowPaymentDue] = useState(false);

  const { data: OrganizationData } = useGetAgencyOrganizations();
  const { data: cardDetails, isLoading: isLoadingCards } = useGetCardDetails();
  const { mutate: payAgencyDues, isPending: payingAgencyDues } = usePayAgencyDues();

  const accountStatus = useMemo(() => OrganizationData?.account_status, [OrganizationData?.account_status]);
  const isPastDue = accountStatus === 'past_due';
  const isInactiveOrArchived = accountStatus === 'inactive' || accountStatus === 'archived';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePayNow = () => {
    payAgencyDues(undefined, {
      onSuccess: () => {
        setShowPaymentDue(false);
      }
    });
  };

  useEffect(() => {
    if (isPastDue && location.pathname !== '/agency/billing') {
      navigate('/agency/billing', { replace: true });
    }
  }, [isPastDue, location.pathname, navigate]);

  useEffect(() => {
    if (isInactiveOrArchived && location.pathname !== '/access-denied') {
      navigate('/access-denied', { replace: true });
    }
  }, [isInactiveOrArchived, location.pathname, navigate]);

  useEffect(() => {
    if (isPastDue) {
      setShowPaymentDue(true);
      setShowPaymentWarning(false);
    } else {
      setShowPaymentDue(false);
    }
  }, [isPastDue]);

  useEffect(() => {
    if (showPaymentDue) return;
    const isReminderDismissed = getCookie(PAYMENT_CARD_REMINDER_COOKIE);
    const hasNoCards = !isLoadingCards && (!cardDetails?.data || cardDetails?.data?.length === 0);
    const isBillingEnabled = Boolean(OrganizationData?.billing_enabled);

    if (isBillingEnabled && hasNoCards && !isReminderDismissed && !isBillingPage) {
      setShowPaymentWarning(true);
    } else {
      setShowPaymentWarning(false);
    }
  }, [cardDetails, isLoadingCards, OrganizationData, isBillingPage, showPaymentDue]);

  return (
    <>
      <AgencyHeader onMenuClick={handleDrawerToggle} />
      <Box bgcolor={theme.palette.background.default} display='flex' sx={{ height: '100vh' }}>
        {!isProfilePage && (
          <AgencySidebar isPausedPayment={isPastDue} isMenuOpen={mobileOpen} handleClose={handleDrawerToggle} />
        )}
        <Box
          component='main'
          sx={
            isProfilePage
              ? { flexGrow: 1, overflowX: 'hidden' }
              : {
                  flexGrow: 1,
                  padding: isOnboardingFormEdit ? 0 : { xs: 2, lg: 3 },
                  paddingTop: { xs: '72px', lg: '88px' },
                  overflowX: 'hidden',
                  height: '100%'
                }
          }
        >
          {children}
        </Box>
      </Box>

      <PaymentDueModal
        open={showPaymentDue}
        onClose={() => setShowPaymentDue(false)}
        onPayNow={handlePayNow}
        paying={payingAgencyDues}
      />

      <PaymentCardWarningModal
        open={showPaymentWarning}
        onClose={() => setShowPaymentWarning(false)}
        billingType='agency'
      />
    </>
  );
}
